import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Youtube, Linkedin, Send, Lock } from 'lucide-react';
import Logo from './Logo';
import { brand, serviceCategories } from '../mock';
import { toast } from '../hooks/use-toast';
import { useLang } from '../i18n/LanguageContext';

const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState('');

  const onSubscribe = (e) => {
    e.preventDefault();
    if (!email) return;
    toast({
      title: 'Subscribed!',
      description: `Thanks \u2014 we\u2019ll keep ${email} posted on Gold Care Esthetic news.`,
    });
    setEmail('');
  };

  const linkColumns = [
    {
      title: t('footer.services'),
      links: serviceCategories.map((c) => ({
        label: t(`cat.${c.id}`, c.label),
        href: `#${c.id}`,
      })),
    },
    {
      title: t('footer.about'),
      links: [
        { label: t('nav.about'), href: '#about' },
        { label: t('loc.kicker'), href: '#locations' },
        { label: t('nav.blog'), href: '#blog' },
        { label: t('nav.contact'), href: '#contact' },
      ],
    },
  ];

  return (
    <footer id="contact" className="bg-[#072A1F] border-t border-[#D4AF37]/15 pt-20 pb-10 text-[#ffffff]">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 pb-14 border-b border-[#D4AF37]/15">
          {/* Brand */}
          <div className="lg:col-span-4">
            <Logo size="lg" withMark />
            <p className="text-[#ffffff]/70 mt-6 leading-relaxed max-w-sm text-[15px]">
              {t('footer.aboutDesc')}
            </p>

            <form onSubmit={onSubscribe} className="mt-7 flex max-w-sm">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('contact.email')}
                className="flex-1 px-5 py-3 bg-[#124D3A] border border-[#D4AF37]/25 rounded-l-full text-sm text-[#ffffff] placeholder:text-[#ffffff]/40 focus:outline-none focus:border-[#D4AF37]"
              />
              <button
                type="submit"
                className="btn-gold px-6 rounded-r-full flex items-center gap-2 text-sm"
              >
                <Send className="w-4 h-4" />
                {t('cta.apply', 'Subscribe')}
              </button>
            </form>

            <div className="flex gap-3 mt-7">
              {[
                { Icon: Instagram, href: brand.instagram, label: 'Instagram' },
                { Icon: Facebook, href: '#fb', label: 'Facebook' },
                { Icon: Youtube, href: '#yt', label: 'YouTube' },
                { Icon: Linkedin, href: '#li', label: 'LinkedIn' },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  aria-label={label}
                  className="w-10 h-10 rounded-full bg-[#124D3A] border border-[#D4AF37]/20 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B3D2E] transition-colors"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {linkColumns.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.label}>
                    <a
                      href={l.href}
                      className="text-sm text-[#ffffff]/70 hover:text-[#D4AF37] transition-colors"
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Locations */}
          <div className="lg:col-span-4">
            <h4 className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37] mb-5">{t('loc.kicker')}</h4>
            <div className="grid sm:grid-cols-2 gap-5">
              {brand.locations.map((loc) => (
                <div key={loc.id} className="text-sm text-[#ffffff]/75 space-y-2">
                  <div className="flex items-center gap-2 font-medium text-[#ffffff]">
                    <span className="text-base">{loc.flag}</span>
                    <span>{loc.country}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-1 shrink-0" />
                    <a
                      href={loc.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-[#D4AF37] transition-colors leading-relaxed"
                    >
                      {t(`loc.${loc.id}.addr`, loc.address)}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
                    <a href={`tel:${loc.tel}`} className="hover:text-[#D4AF37] transition-colors">
                      {loc.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-6 text-sm text-[#ffffff]/75">
              <Mail className="w-4 h-4 text-[#D4AF37]" />
              <a href={`mailto:${brand.email}`} className="hover:text-[#D4AF37] transition-colors">
                {brand.email}
              </a>
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row items-center justify-between gap-4 pt-8 text-xs text-[#ffffff]/55">
          <div>© {new Date().getFullYear()} Gold Care Esthetic. {t('footer.rights')}</div>
          <div className="flex gap-6 items-center">
            <a href="#privacy" className="hover:text-[#D4AF37]">{t('footer.privacy')}</a>
            <a href="#terms" className="hover:text-[#D4AF37]">{t('footer.terms')}</a>
            <Link
              to="/admin/login"
              data-testid="footer-admin-link"
              className="inline-flex items-center gap-1.5 hover:text-[#D4AF37]"
              title="Admin login"
            >
              <Lock className="w-3 h-3" />
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
